"use client";

import React from "react";
import Link from "next/link";
import Image from "next/image";
import { BrandedHeader } from "@/components/branded/headers/BrandedHeader";
import type { ResolvedHeaderColors } from "@/features/theme/resolveBrandedStyle";
import type { AuthorWithCount } from "@/utilities/data/getAuthors";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useLabels } from "@/lib/labels/LabelsProvider";

interface AuthorsLayoutDefaultProps {
    authors: AuthorWithCount[];
    title: string;
    subtitle: string;
    headerColors?: ResolvedHeaderColors;
    breadcrumbs?: React.ReactNode;
}

export function AuthorsLayoutDefault({ authors, title, subtitle, headerColors, breadcrumbs }: AuthorsLayoutDefaultProps) {
    const labels = useLabels();
    const urlBuilder = useNavigation();

    return (
        <div className="min-h-screen">
            <BrandedHeader
                titleAs="h1"
                title={title}
                subtitle={subtitle}
                colors={headerColors?.colors}
                backgroundImageUrl={headerColors?.backgroundImageUrl}
            />

            {breadcrumbs}

            <div className="max-w-350 mx-auto px-4 py-8">
                {authors.length === 0 ? (
                    <p className="text-center text-muted-foreground py-12">{labels.authorsPage.noAuthors}</p>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {authors.map((author) => {
                            const avatarUrl =
                                author.avatar && typeof author.avatar !== "number" ? author.avatar.url : null;
                            const initials = author.name
                                .split(/\s+/)
                                .map((part) => part[0])
                                .filter(Boolean)
                                .slice(0, 2)
                                .join("")
                                .toUpperCase();

                            return (
                                <Link
                                    prefetch={false}
                                    key={author.id}
                                    href={urlBuilder.author(author.slug ?? "")}
                                    className="block rounded-lg border border-border bg-card p-6 text-center hover:shadow-md transition-shadow"
                                >
                                    <div className="relative mx-auto mb-4 h-24 w-24 overflow-hidden rounded-full bg-primary/10">
                                        {avatarUrl ? (
                                            <Image
                                                src={avatarUrl}
                                                alt={author.name}
                                                fill
                                                sizes="96px"
                                                className="object-cover"
                                            />
                                        ) : (
                                            <div className="flex h-full w-full items-center justify-center text-2xl font-semibold text-primary">
                                                {initials || "?"}
                                            </div>
                                        )}
                                    </div>
                                    <h2 className="text-lg font-semibold mb-1 text-card-foreground">{author.name}</h2>
                                    {author.bio && (
                                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{author.bio}</p>
                                    )}
                                    <span className="inline-block text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded">
                                        {author.postCount} {author.postCount === 1 ? "post" : "posts"}
                                    </span>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
